import { Bell, Loader2 } from "lucide-react";
import { useState, type FC } from "react";
import { toast } from "sonner";

import { OutgoingIntakesAPI } from "@/api/outgoingIntakesApi";
import type {
  OutgoingIntake,
  OutgoingIntakeReminderSchedule
} from "@/api/types/outgoingIntake";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";

import { useRequestsIde } from "../context/RequestsIdeContext";
import { useRequestsData } from "../data/RequestsDataContext";
import { clockLabel } from "../model";
import { useIntakeActions } from "../useIntakeActions";
import {
  CustomReminders,
  ReminderPresetSelect,
  useReminderCadence
} from "./ReminderCadence";
import { describeReminders } from "./reminders";
import { DEFAULT_REMINDERS } from "./useIntakeDraft";

type Props = {
  intake: OutgoingIntake;
  clientFirstName: string;
};

/** Remind now with an optional message; see the last reminder; set the automatic cadence. */
export const RemindPopover: FC<Props> = ({ intake, clientFirstName }) => {
  const { clientId } = useRequestsIde();
  const data = useRequestsData();
  const { remind } = useIntakeActions();
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [savingAuto, setSavingAuto] = useState(false);

  const auto = intake.reminder_schedule ?? null;
  const last = intake.last_reminded_at;
  const count = intake.reminder_count ?? 0;

  const saveSchedule = async (
    reminder_schedule: OutgoingIntakeReminderSchedule | null
  ) => {
    setSavingAuto(true);
    try {
      data.applyIntake(
        await OutgoingIntakesAPI.patch(clientId, intake.outgoing_intake_id, {
          reminder_schedule
        })
      );
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Couldn't change automatic reminders"
      );
    } finally {
      setSavingAuto(false);
    }
  };

  const cadence = useReminderCadence(auto, (next) => void saveSchedule(next));

  const send = async () => {
    setSending(true);
    const ok = await remind(intake, note);
    setSending(false);
    if (ok) {
      setNote("");
      setOpen(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="primary" size="sm" className="h-7 gap-1.5 text-xs">
          <Bell className="size-3.5" /> Remind
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-96 p-0"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <div className="flex flex-col gap-0.5 border-b border-border px-3 py-2.5">
          <span className="text-sm font-medium">Remind {clientFirstName}</span>
          <span className="text-xs text-muted-foreground">
            {last
              ? `Last reminded ${clockLabel(last)}${
                  count > 1 ? ` · ${count} reminders so far` : ""
                }`
              : "No reminders sent yet"}
          </span>
        </div>
        <div className="flex flex-col gap-3 px-3 py-3">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="remind-note" className="text-xs">
              Add a message{" "}
              <span className="font-normal text-muted-foreground">
                (optional)
              </span>
            </Label>
            <Textarea
              id="remind-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              placeholder={`Hi ${clientFirstName}, just a reminder to finish this when you have a minute.`}
              className="resize-none text-xs"
            />
          </div>
          <div className="flex items-start justify-between gap-3">
            <div className="flex flex-col gap-0.5">
              <Label htmlFor="remind-auto" className="text-xs">
                Remind automatically
              </Label>
              <span className="text-xs text-muted-foreground">
                {auto
                  ? describeReminders(auto)
                  : "Off. Only the reminders you send."}
              </span>
            </div>
            <Switch
              id="remind-auto"
              checked={auto !== null}
              disabled={savingAuto}
              onCheckedChange={(on) =>
                void saveSchedule(on ? DEFAULT_REMINDERS : null)
              }
            />
          </div>
          {auto ? (
            <ReminderPresetSelect
              label="Schedule"
              value={auto}
              presetId={cadence.presetId}
              onChoose={cadence.choose}
              disabled={savingAuto}
            />
          ) : null}
          {auto && cadence.custom ? (
            <CustomReminders
              value={auto}
              onChange={(next) => void saveSchedule(next)}
              disabled={savingAuto}
            />
          ) : null}
        </div>
        <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-2">
          <span className="text-xs text-muted-foreground">
            Uses the same link
          </span>
          <div className="flex gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs"
              onClick={() => setOpen(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              className="h-7 text-xs"
              disabled={sending}
              onClick={() => void send()}
            >
              {sending ? <Loader2 className="size-3.5 animate-spin" /> : null}
              Send reminder
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};
